import { NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { CheckedComponent, formatPhotoValueForForm, IPhotoForm, photoFormBuilder } from '@curbnturf/form-helpers';
import { listingComplete } from '@curbnturf/helpers';
import { Logger } from '@curbnturf/network';
import { IPhoto, Listing, Site } from '@curbnturf/objects';
import { SignUpStateFacade } from '@curbnturf/sign-up-state';
import { SiteService } from '@curbnturf/site';
import { SitePhotosComponent } from '@curbnturf/site-components/src/lib/site-photos/site-photos.component';
import * as SiteActions from '@curbnturf/site/src/lib/+state/site.actions';
import { IonicModule, ModalController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';

@Component({
  selector: 'curbnturf-edit-site-photos-modal',
  standalone: true,
  imports: [CheckedComponent, IonicModule, NgIf, ReactiveFormsModule, SitePhotosComponent],
  templateUrl: './edit-site-photos-modal.component.html',
  styleUrl: './edit-site-photos-modal.component.css',
})
export class EditSitePhotosModalComponent implements OnInit {
  @Input() site: Site;

  subscriptions: Subscription[] = [];

  form: FormGroup<{ photos: FormArray<FormGroup<IPhotoForm>> }>;

  listingComplete = listingComplete;

  get mainFormValue(): Listing {
    if (this.form) {
      return this.form.value as unknown as Listing;
    }
    return new Listing();
  }

  constructor(
    private fb: FormBuilder,
    private modalController: ModalController,
    private signUpFacade: SignUpStateFacade,
    private actions$: Actions,
    private siteService: SiteService,
    private alertFacade: AlertFacade,
    private logger: Logger,
  ) {}

  async close() {
    if (this.site && this.site.id) {
      this.signUpFacade.updateSitePhotoDetails(this.site.id, this.form.value.photos as IPhoto[]);
    }
    await this.modalController.dismiss();
  }

  ngOnInit() {
    this.makeForms();

    this.actions$.pipe(ofType(SiteActions.siteImagesUpdated)).subscribe((action) => {
      this.updatePhotos(action.photos);
    });
  }

  updatePhotos(photos: IPhoto[]) {
    this.syncArrayField(
      photos?.map((photo) => formatPhotoValueForForm(photo)),
      this.form.controls.photos as FormArray<FormGroup<IPhotoForm>>,
      photoFormBuilder,
    );
  }

  makeForms() {
    this.form = this.fb.group({ photos: this.fb.array<FormGroup<IPhotoForm>>([]) });

    if (this.site) {
      this.updatePhotos(this.site.photos ?? []);
    }
  }

  protected syncArrayField(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    source?: any[],
    destination?: FormArray,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formBuilder?: (formBuilder: FormBuilder, argv?: any) => FormGroup,
  ) {
    if (source && destination) {
      // Make sure the form array is at least as short as the incoming array
      while (source.length < destination.length) {
        destination.removeAt(destination.length - 1);
      }
      // Add or replace each element in the from array with the incoming element
      source.forEach((sourceEl, index) => {
        if (destination) {
          if (destination.length > index) {
            destination.controls[index].setValue(sourceEl);
          } else {
            if (formBuilder) {
              destination.push(formBuilder(this.fb, sourceEl));
            } else {
              destination.push(this.fb.control(sourceEl));
            }
          }
        }
      });
    }
  }

  onImageUploaded() {
    if (this.site && this.site.id) {
      this.siteService.fetch(this.site.id).subscribe({
        next: (site) => {
          this.site = new Site(site);
          this.makeForms();
        },
        error: (err) => {
          this.logger.error('Error while fetching site', err);
          this.alertFacade.display({
            title: 'An Error Occurred While Uploading Image.',
            body: 'An Error Occurred While Uploading Image. If the problem persists, please contact support.',
            level: 'error',
          });
        },
      });
    }
  }
}
